/* ============================================
   AIChatPanel — Chatbot flotante de Marco
   Conecta con chatbot-respond via agent_slug
   Features: historial, progreso, stop, persistencia
   ============================================ */
import { useState, useEffect, useRef, useCallback } from 'react'
import {
  IconSend, IconX, IconPlus, IconClock,
} from '@/components/icons/Icons'
import ArtifactPanel from './ArtifactPanel'

type ChatSize = 'normal' | 'expanded'
import { supabase } from '@/lib/supabase'
import type { SocialStrategy, SocialPost, MarcoConversation, MarcoMessage, ChatArtifact } from '@/lib/types'

interface AIChatPanelProps {
  isOpen: boolean
  onToggle: () => void
  strategy: SocialStrategy | null
  onPostGenerated: (post: Partial<SocialPost>) => void
  onStrategyUpdated?: () => void
  onCalendarProposalCreated?: () => void
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  suggestedActions?: { label: string; action: string }[]
  artifact?: ChatArtifact | null
}

const AGENT_SLUG = 'marco'
const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

const DEFAULT_MARCO_AVATAR = '/assets/brand/marco-avatar.svg'

// Quick actions (welcome screen)
const QUICK_ACTIONS = [
  { icon: 'target', label: 'Estrategia', prompt: 'Quiero definir mi estrategia de contenido para redes sociales' },
  { icon: 'edit', label: 'Caption', prompt: 'Genera un caption para un post de Instagram sobre ' },
  { icon: 'calendar', label: 'Plan semanal', prompt: 'Genera un plan de contenido para esta semana' },
  { icon: 'refresh', label: 'Multi-canal', prompt: 'Adapta este contenido para todas las redes sociales: ' },
  { icon: 'hash', label: 'Hashtags', prompt: 'Sugiere los mejores hashtags para un post sobre ' },
  { icon: 'chart', label: 'Tendencias', prompt: 'Cuales son las tendencias actuales en redes sociales para mi sector?' },
]

// Tools menu options (Gemini-style)
type ToolMode = 'brainstorm' | 'create_posts' | 'strategy' | 'calendar' | 'captions' | 'image_prompt'

function getToolIcon(mode: ToolMode, size = 18): React.ReactNode {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  switch (mode) {
    case 'brainstorm': return <svg {...props}><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
    case 'create_posts': return <svg {...props}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
    case 'strategy': return <svg {...props}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
    case 'calendar': return <svg {...props}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
    case 'captions': return <svg {...props}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
    case 'image_prompt': return <svg {...props}><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>
  }
}

const TOOL_MODE_LABELS: Record<ToolMode, { label: string; description: string }> = {
  brainstorm:    { label: 'Brainstorming', description: 'Genera ideas y nutre un artifact' },
  create_posts:  { label: 'Crear publicaciones', description: 'Propuestas de posts para redes' },
  strategy:      { label: 'Estrategia de marca', description: 'Voz, audiencia, pilares, hashtags' },
  calendar:      { label: 'Calendario editorial', description: 'Plan de contenido semanal/mensual' },
  captions:      { label: 'Generar captions', description: 'Textos para publicaciones' },
  image_prompt:  { label: 'Prompt de imagen', description: 'Prompts para imágenes IA' },
}

const TOOL_MODES: ToolMode[] = ['brainstorm', 'create_posts', 'strategy', 'calendar', 'captions', 'image_prompt']

function getActionIcon(icon: string) {
  switch (icon) {
    case 'target': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
    case 'edit': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
    case 'calendar': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
    case 'refresh': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
    case 'hash': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
    case 'chart': return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
    default: return null
  }
}

// Progress steps shown while Marco thinks
const PROGRESS_STEPS = [
  { delay: 0, text: 'Analizando solicitud...' },
  { delay: 3000, text: 'Activando workers...' },
  { delay: 8000, text: 'Generando respuesta...' },
  { delay: 15000, text: 'Procesando contenido...' },
]

// Relative time formatter
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Ahora'
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d`
  return new Date(dateStr).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })
}

async function sendToMarco(
  message: string,
  sessionId: string,
  _authToken: string,
  signal?: AbortSignal,
  toolMode?: ToolMode | null,
): Promise<{ reply: string; strategyUpdated: boolean; postsCreated: number; avatarUrl?: string; artifact?: ChatArtifact | null }> {
  const bodyPayload = {
      session_id: sessionId,
      message,
      agent_slug: AGENT_SLUG,
      ...(toolMode ? { tool_mode: toolMode } : {}),
    }
  console.log('[Marco Send]', { toolMode, body: bodyPayload })
  const res = await fetch(`${API_URL}/api/widget/chatbot`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(bodyPayload),
    signal,
  })

  if (!res.ok) {
    const errText = await res.text()
    throw new Error(`API error ${res.status}: ${errText}`)
  }

  const data = await res.json()
  return {
    reply: data.response || data.reply || 'Sin respuesta',
    strategyUpdated: !!data.strategy_updated,
    postsCreated: data.posts_created || 0,
    avatarUrl: data.avatar_url || undefined,
    artifact: data.artifact || null,
  }
}

export default function AIChatPanel({
  isOpen,
  onToggle,
  strategy: _strategy,
  onPostGenerated: _onPostGenerated,
  onStrategyUpdated,
  onCalendarProposalCreated,
}: AIChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sessionId, setSessionId] = useState(() => {
    const saved = localStorage.getItem('marco_session_id')
    return saved || `marco-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  })
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Conversation history
  const [showHistory, setShowHistory] = useState(false)
  const [conversations, setConversations] = useState<MarcoConversation[]>([])
  const [activeConvId, setActiveConvId] = useState<number | null>(null)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [marcoAvatar, setMarcoAvatar] = useState(DEFAULT_MARCO_AVATAR)

  // Artifact panel
  const [activeArtifact, setActiveArtifact] = useState<ChatArtifact | null>(null)

  // Tools menu
  const [activeToolMode, setActiveToolMode] = useState<ToolMode | null>(null)
  const [showToolsMenu, setShowToolsMenu] = useState(false)
  const toolsMenuRef = useRef<HTMLDivElement>(null)

  // Progress indicator
  const [progressStep, setProgressStep] = useState(0)
  const progressTimers = useRef<ReturnType<typeof setTimeout>[]>([])

  // ─── Expand / Resize (Gmail-style) ───
  const [chatSize, setChatSize] = useState<ChatSize>('normal')
  const [manualSize, setManualSize] = useState<{ w: number; h: number } | null>(null)
  const chatRef = useRef<HTMLDivElement>(null)
  const isResizing = useRef(false)

  const toggleExpand = useCallback(() => {
    setManualSize(null) // reset manual resize when toggling
    setChatSize(prev => prev === 'expanded' ? 'normal' : 'expanded')
  }, [])

  // Drag-to-resize handler (top-left corner)
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    isResizing.current = true

    const startX = e.clientX
    const startY = e.clientY
    const chatEl = chatRef.current
    if (!chatEl) return

    const rect = chatEl.getBoundingClientRect()
    const startW = rect.width
    const startH = rect.height

    const onMove = (ev: MouseEvent) => {
      if (!isResizing.current) return
      // Dragging top-left: moving left increases width, moving up increases height
      const dw = startX - ev.clientX
      const dh = startY - ev.clientY
      const newW = Math.max(340, Math.min(startW + dw, window.innerWidth - 48))
      const newH = Math.max(400, Math.min(startH + dh, window.innerHeight - 48))
      setManualSize({ w: newW, h: newH })
    }

    const onUp = () => {
      isResizing.current = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'nwse-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [])

  // Get auth token
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.access_token) {
        setAuthToken(data.session.access_token)
        setUserId(null) // User ID not available from session token
      }
    })
  }, [])

  // Load Marco avatar from DB on mount
  useEffect(() => {
    supabase.from('agente_config').select('avatar_sm_url,avatar_url').eq('slug', 'marco').single()
      .then(({ data }: any) => {
        if (data?.avatar_sm_url) setMarcoAvatar(data.avatar_sm_url)
        else if (data?.avatar_url) setMarcoAvatar(data.avatar_url)
      }).catch(() => {})
  }, [])

  // Auto scroll — bulletproof: immediate + rAF + delayed
  const scrollToBottom = useCallback(() => {
    const el = messagesContainerRef.current
    if (!el) return
    const doScroll = () => { el.scrollTop = el.scrollHeight }
    doScroll()
    requestAnimationFrame(doScroll)
    setTimeout(doScroll, 100)
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, progressStep, scrollToBottom])

  // Persist sessionId to localStorage
  useEffect(() => {
    localStorage.setItem('marco_session_id', sessionId)
  }, [sessionId])

  // Focus input on open + auto-load last conversation
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200)
      // Auto-load messages if we have a saved session and no messages yet
      if (messages.length === 0 && !activeConvId) {
        supabase
          .from('conversaciones')
          .select('id, chat_id')
          .eq('canal', 'web_chat')
          .eq('chat_id', sessionId)
          .limit(1)
          .then(({ data }: { data: Array<{ id: number; chat_id: string }> | null }) => {
            if (data && data.length > 0) {
              loadMessages(data[0].id, data[0].chat_id).then(() => {
                // Wait for React to re-render with loaded messages
                setTimeout(scrollToBottom, 200)
              })
            }
          })
      }
    }
  }, [isOpen, userId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load conversations list
  const loadConversations = useCallback(async () => {
    setLoadingHistory(true)
    try {
      const { data } = await supabase
        .from('conversaciones')
        .select('id, chat_id, estado, ultimo_mensaje_at, agent_slug, created_at, metadata')
        .eq('canal', 'web_chat')
        .eq('agent_slug', 'marco')
        .order('ultimo_mensaje_at', { ascending: false, nullsFirst: false })
        .limit(20)
      if (data) setConversations(data as MarcoConversation[])
    } catch (err) {
      console.error('Error loading conversations:', err)
    }
    setLoadingHistory(false)
  }, [])

  // Load messages for a conversation
  const loadMessages = useCallback(async (convId: number, chatId: string) => {
    try {
      const { data } = await supabase
        .from('mensajes')
        .select('id, conversacion_id, remitente, contenido, created_at')
        .eq('conversacion_id', convId)
        .order('created_at', { ascending: true })
        .limit(100)

      if (data) {
        const mapped: ChatMessage[] = (data as MarcoMessage[]).map(m => ({
          role: m.direccion === 'entrante' || (m as any).remitente === 'cliente' ? 'user' as const : 'assistant' as const,
          content: m.contenido || '',
          timestamp: (m as any).created_at || m.fecha_mensaje || '',
        }))
        setMessages(mapped)
        setSessionId(chatId)
        setActiveConvId(convId)
        setShowHistory(false)
      }
    } catch (err) {
      console.error('Error loading messages:', err)
    }
  }, [])

  // Start new conversation
  const handleNewConversation = useCallback(() => {
    const newId = `marco-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setMessages([])
    setSessionId(newId)
    localStorage.setItem('marco_session_id', newId)
    setActiveConvId(null)
    setShowHistory(false)
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [])

  // Progress animation
  const startProgress = useCallback(() => {
    setProgressStep(0)
    progressTimers.current.forEach(clearTimeout)
    progressTimers.current = []
    PROGRESS_STEPS.forEach((step, i) => {
      if (i > 0) {
        const timer = setTimeout(() => setProgressStep(i), step.delay)
        progressTimers.current.push(timer)
      }
    })
  }, [])

  const stopProgress = useCallback(() => {
    progressTimers.current.forEach(clearTimeout)
    progressTimers.current = []
    setProgressStep(0)
  }, [])

  // Stop / cancel request
  const handleStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
    stopProgress()
    setIsLoading(false)
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Respuesta cancelada.',
      timestamp: new Date().toISOString(),
    }])
  }, [stopProgress])



  // Send message (accepts optional override text for action buttons)
  const handleSend = useCallback(async (actionText?: string) => {
    const text = (actionText || input).trim()
    if (!text || isLoading || !authToken) return

    const userMsg: ChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    }

    setMessages(prev => [...prev, userMsg])
    setInput('')
    setIsLoading(true)
    startProgress()

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const { reply, strategyUpdated, postsCreated, avatarUrl, artifact } = await sendToMarco(text, sessionId, authToken, controller.signal, activeToolMode)
      if (avatarUrl) setMarcoAvatar(avatarUrl)

      // Parse suggested actions from JSON response
      let displayReply = reply
      let suggestedActions: { label: string; action: string }[] | undefined
      try {
        const jsonBlock = reply.match(/```(?:json)?\s*([\s\S]*?)```/)
        const jsonStr = jsonBlock ? jsonBlock[1].trim() : reply
        const parsed = JSON.parse(jsonStr)
        if (parsed.suggested_actions && Array.isArray(parsed.suggested_actions)) {
          displayReply = parsed.reply || reply
          suggestedActions = parsed.suggested_actions.map((a: any) =>
            typeof a === 'string' ? { label: a, action: a } : a
          )
        }
      } catch { /* not JSON */ }

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: displayReply,
        timestamp: new Date().toISOString(),
        suggestedActions,
        artifact: artifact || undefined,
      }
      setMessages(prev => [...prev, assistantMsg])

      // Auto-open artifact panel when a new artifact arrives
      if (artifact) {
        setActiveArtifact(artifact)
        // Delayed scroll to ensure artifact card is visible
        setTimeout(scrollToBottom, 300)
      }

      if (strategyUpdated && onStrategyUpdated) {
        onStrategyUpdated()
      }

      if (postsCreated > 0 && onCalendarProposalCreated) {
        onCalendarProposalCreated()
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        // User cancelled — already handled in handleStop
        return
      }
      console.error('Marco chat error:', err)
      const errorMsg: ChatMessage = {
        role: 'assistant',
        content: 'Lo siento, hubo un error al procesar tu mensaje. Intenta de nuevo.',
        timestamp: new Date().toISOString(),
      }
      setMessages(prev => [...prev, errorMsg])
    }

    abortRef.current = null
    stopProgress()
    setIsLoading(false)
  }, [input, isLoading, authToken, sessionId, onStrategyUpdated, onCalendarProposalCreated, startProgress, stopProgress])

  // Quick action
  function handleQuickAction(prompt: string) {
    setInput(prompt)
    inputRef.current?.focus()
  }

  // Key handler
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Toggle history
  const handleToggleHistory = useCallback(() => {
    if (!showHistory) {
      loadConversations()
    }
    setShowHistory(prev => !prev)
  }, [showHistory, loadConversations])

  if (!isOpen) return null

  // Compute inline size overrides
  const sizeClass = manualSize ? '' : `marco-chat--${chatSize}`
  const inlineStyle: React.CSSProperties = manualSize
    ? { width: manualSize.w, maxHeight: manualSize.h, height: manualSize.h }
    : {}

  return (
    <>
    <div ref={chatRef} className={`marco-chat ${sizeClass}`} style={inlineStyle} {...(manualSize ? { 'data-resized': '' } : {})}>
      {/* Resize handle — top-left corner */}
      <div
        className="marco-chat__resize-handle"
        onMouseDown={handleResizeStart}
        title="Arrastrar para redimensionar"
      />
      {/* Header */}
      <div className="marco-chat__header">
        <div className="marco-chat__identity">
          <img
            src={marcoAvatar}
            alt="Marco"
            className="marco-chat__avatar"
          />
          <div>
            <div className="marco-chat__name">Marco</div>
            <div className="marco-chat__role">Content Strategist</div>
          </div>
        </div>
        <div className="marco-chat__toolbar">
          <button
            type="button"
            className="marco-chat__tool-btn"
            onClick={handleNewConversation}
            title="Nueva conversación"
          >
            <IconPlus size={16} />
          </button>
          <button
            type="button"
            className="marco-chat__tool-btn"
            onClick={handleToggleHistory}
            title="Historial"
          >
            <IconClock size={16} />
          </button>
          {/* Expand / Collapse toggle (Gmail-style) */}
          <button
            type="button"
            className="marco-chat__tool-btn"
            onClick={toggleExpand}
            title={chatSize === 'expanded' ? 'Reducir' : 'Expandir'}
          >
            {chatSize === 'expanded' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="marco-chat__close"
            onClick={onToggle}
            title="Cerrar"
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      {/* History drawer */}
      {showHistory && (
        <div className="marco-chat__drawer">
          <div className="marco-chat__drawer-header">
            <button
              type="button"
              className="marco-chat__tool-btn"
              onClick={() => setShowHistory(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span>Conversaciones</span>
            <button
              type="button"
              className="marco-chat__tool-btn"
              onClick={() => { handleNewConversation(); }}
              title="Nueva conversación"
            >
              <IconPlus size={16} />
            </button>
          </div>
          <div className="marco-chat__drawer-list">
            {loadingHistory && (
              <div className="marco-chat__drawer-loading">
                <div className="auth-spinner" style={{ width: 20, height: 20 }} />
              </div>
            )}
            {!loadingHistory && conversations.length === 0 && (
              <div className="marco-chat__drawer-empty">
                No hay conversaciones anteriores
              </div>
            )}
            {conversations.map(conv => (
              <button
                key={conv.id}
                type="button"
                className={`marco-chat__conv-item${activeConvId === conv.id ? ' marco-chat__conv-item--active' : ''}`}
                onClick={() => loadMessages(conv.id, conv.chat_id)}
              >
                <div className="marco-chat__conv-preview">
                  {conv.ultimo_mensaje
                    ? (conv.ultimo_mensaje.length > 60
                        ? conv.ultimo_mensaje.slice(0, 60) + '…'
                        : conv.ultimo_mensaje)
                    : 'Conversación vacía'}
                </div>
                <div className="marco-chat__conv-time">
                  {conv.ultimo_mensaje_at ? timeAgo(conv.ultimo_mensaje_at) : timeAgo(conv.created_at)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      {!showHistory && (
        <div ref={messagesContainerRef} className="marco-chat__messages">
          {messages.length === 0 && (
            <div className="marco-chat__welcome">
              <img
                src={marcoAvatar}
                alt="Marco"
                className="marco-chat__welcome-avatar"
              />
              <h4>Hola, soy Marco</h4>
              <p>
                Tu Content Strategist. Puedo ayudarte a crear posts,
                generar captions, planificar tu calendario editorial y mas.
              </p>
              <div className="marco-chat__actions">
                {QUICK_ACTIONS.map((action, i) => (
                  <button
                    key={i}
                    type="button"
                    className="marco-chat__action-btn"
                    onClick={() => handleQuickAction(action.prompt)}
                  >
                    {getActionIcon(action.icon)}
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`marco-chat__msg ${
                msg.role === 'user' ? 'marco-chat__msg--user' : 'marco-chat__msg--assistant'
              }`}
            >
              {msg.role === 'assistant' && (
                <img src={marcoAvatar} alt="" className="marco-chat__msg-avatar" />
              )}
              <div className="marco-chat__bubble">
                {msg.content.split('\n').map((line, li) => (
                  <span key={li}>
                    {line}
                    {li < msg.content.split('\n').length - 1 && <br />}
                  </span>
                ))}
                {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {msg.suggestedActions.map((action, ai) => (
                      <button
                        key={ai}
                        type="button"
                        onClick={() => handleSend(action.label)}
                        style={{
                          padding: '6px 14px', borderRadius: 16, border: '1px solid var(--border-color)',
                          background: 'var(--bg-surface)', color: 'var(--color-primary)',
                          fontSize: 12, fontWeight: 500, cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {msg.artifact && (
                <div
                  className="marco-artifact-card"
                  onClick={() => setActiveArtifact(msg.artifact!)}
                >
                  <div className="marco-artifact-card__icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </div>
                  <div className="marco-artifact-card__info">
                    <span className="marco-artifact-card__title">{msg.artifact.titulo}</span>
                    <span className="marco-artifact-card__meta">v{msg.artifact.version} · {msg.artifact.tipo}</span>
                  </div>
                  <svg className="marco-artifact-card__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              )}
            </div>
          ))}

          {/* Progress indicator while loading */}
          {isLoading && (
            <div className="marco-chat__msg marco-chat__msg--assistant">
              <img src={marcoAvatar} alt="" className="marco-chat__msg-avatar" />
              <div className="marco-chat__bubble marco-chat__bubble--progress">
                <div className="marco-chat__progress">
                  <div className="marco-chat__progress-spinner" />
                  <span className="marco-chat__progress-text">
                    {PROGRESS_STEPS[progressStep]?.text || 'Procesando...'}
                  </span>
                </div>
              </div>
            </div>
          )}


        </div>
      )}

      {/* Input */}
      <div className="marco-chat__input-area">
        {/* Tool mode badge */}
        {activeToolMode && (
          <div className="marco-tool-badge">
            <span className="marco-tool-badge__icon">{getToolIcon(activeToolMode, 14)}</span>
            <span>{TOOL_MODE_LABELS[activeToolMode].label}</span>
            <button type="button" onClick={() => setActiveToolMode(null)} className="marco-tool-badge__close">×</button>
          </div>
        )}
        <div className="marco-chat__input-row">
          {/* Tools + button */}
          <div style={{ position: 'relative' }} ref={toolsMenuRef}>
            <button
              type="button"
              className={`marco-tools-btn ${showToolsMenu ? 'marco-tools-btn--active' : ''}`}
              onClick={() => setShowToolsMenu(!showToolsMenu)}
              title="Herramientas"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            {showToolsMenu && (
              <div className="marco-tools-menu">
                {TOOL_MODES.map(mode => (
                  <button
                    key={mode}
                    type="button"
                    className={`marco-tools-menu__item ${activeToolMode === mode ? 'marco-tools-menu__item--active' : ''}`}
                    onClick={() => {
                      setActiveToolMode(mode)
                      setShowToolsMenu(false)
                      inputRef.current?.focus()
                    }}
                  >
                    <span className="marco-tools-menu__icon">{getToolIcon(mode)}</span>
                    <div className="marco-tools-menu__text">
                      <span className="marco-tools-menu__label">{TOOL_MODE_LABELS[mode].label}</span>
                      <span className="marco-tools-menu__desc">{TOOL_MODE_LABELS[mode].description}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="text"
            className="marco-chat__input"
            placeholder={activeToolMode ? `${TOOL_MODE_LABELS[activeToolMode].label}...` : 'Escribe tu mensaje...'}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading || !authToken}
          />
          {isLoading ? (
            <button
              type="button"
              className="marco-chat__stop-btn"
              onClick={handleStop}
              title="Detener"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="marco-chat__send"
              onClick={() => handleSend()}
              disabled={!input.trim() || !authToken}
              title="Enviar"
            >
              <IconSend size={18} />
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Artifact split panel */}
    {activeArtifact && (
      <ArtifactPanel
        artifact={activeArtifact}
        onClose={() => setActiveArtifact(null)}
        onApprove={async (art) => {
          // Apply artifact strategy to social_strategy table
          try {
            const { data: existing } = await supabase.from('social_strategy').select('id').limit(1)
            if (existing && existing.length > 0) {
              await supabase.from('social_strategy').update({ id: existing[0].id, ...art.contenido })
            } else {
              await supabase.from('social_strategy').insert(art.contenido)
            }
            // Update artifact estado
            await supabase.from('chat_artifacts').update({ id: art.id, estado: 'applied' })
            setActiveArtifact({ ...art, estado: 'applied' })
            onStrategyUpdated?.()
            // Notify in chat
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: '✅ ¡Estrategia aplicada! Los datos se guardaron en la pestaña de Estrategia.',
              timestamp: new Date().toISOString(),
            }])
          } catch (err) {
            console.error('Error applying artifact:', err)
          }
        }}
        onRequestChange={(feedback) => {
          setActiveArtifact(null)
          handleSend(feedback)
        }}
      />
    )}
    </>
  )
}
